import { useState, useEffect } from "react";

export default function ArticleImages({ articleId, siteId, initialImages, onPublished }) {
  const [images, setImages] = useState(initialImages || null);
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState("");
  const [error, setError] = useState(null);
  const [refinement, setRefinement] = useState({});
  const [refining, setRefining] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [publishResult, setPublishResult] = useState(null);

  // Taxonomies
  const [taxonomies, setTaxonomies] = useState({ categories: [], tags: [] });
  const [taxonomiesLoading, setTaxonomiesLoading] = useState(false);
  const [taxonomiesError, setTaxonomiesError] = useState(null);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [newTagInput, setNewTagInput] = useState("");
  const [newTagNames, setNewTagNames] = useState([]);
  const [showTaxPanel, setShowTaxPanel] = useState(false);

  const STEPS = [
    { delay: 0, text: "Claude מנתח את המאמר..." },
    { delay: 8000, text: "יוצר תמונה ראשית עם GPT Image..." },
    { delay: 25000, text: "יוצר תמונות לתוך הטקסט..." },
    { delay: 50000, text: "מסיים ושומר..." },
  ];

  // Load taxonomies once, when panel opens
  const loadTaxonomies = async () => {
    if (!siteId || taxonomiesLoading || taxonomies.categories.length > 0) return;
    setTaxonomiesLoading(true);
    setTaxonomiesError(null);
    try {
      const res = await fetch((process.env.PUBLIC_URL || '') + "/api/articles/" + siteId + "/wp-taxonomies");
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "שגיאה " + res.status);
      }
      const data = await res.json();
      setTaxonomies({
        categories: data.categories || [],
        tags: data.tags || [],
      });
    } catch (e) {
      setTaxonomiesError(e.message);
    } finally {
      setTaxonomiesLoading(false);
    }
  };

  useEffect(() => {
    if (showTaxPanel) loadTaxonomies();
    // eslint-disable-next-line
  }, [showTaxPanel]);

  const toggleCategory = (id) => {
    setSelectedCategoryIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleTag = (id) => {
    setSelectedTagIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const addNewTag = () => {
    const name = newTagInput.trim();
    if (!name) return;
    if (newTagNames.includes(name)) {
      setNewTagInput("");
      return;
    }
    setNewTagNames((prev) => [...prev, name]);
    setNewTagInput("");
  };

  const removeNewTag = (name) => {
    setNewTagNames((prev) => prev.filter((x) => x !== name));
  };

  const generateImages = async () => {
    setLoading(true);
    setError(null);
    setImages(null);
    setPublishResult(null);
    const timers = STEPS.map(({ delay, text }) => setTimeout(() => setLoadingStep(text), delay));
    try {
      const res = await fetch((process.env.PUBLIC_URL || '') + "/api/articles/detail/" + articleId + "/generate-images", { method: "POST" });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "שגיאה " + res.status);
      }
      setImages(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      timers.forEach(clearTimeout);
      setLoading(false);
      setLoadingStep("");
    }
  };

  const regenerate = async (imageKey) => {
    setRefining(imageKey);
    try {
      const res = await fetch((process.env.PUBLIC_URL || '') + "/api/articles/detail/" + articleId + "/regenerate-image", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ image_key: imageKey, refinement_he: refinement[imageKey] || "" }),
      });
      if (!res.ok) throw new Error("שגיאה " + res.status);
      const data = await res.json();
      setImages((prev) => {
        if (imageKey === "featured") {
          return { ...prev, featured: { ...prev.featured, b64: data.b64, size_kb: data.size_kb } };
        }
        const idx = parseInt(imageKey.split("_")[1]);
        const newInline = [...prev.inline];
        newInline[idx] = { ...newInline[idx], b64: data.b64, size_kb: data.size_kb };
        return { ...prev, inline: newInline };
      });
      setRefinement((prev) => ({ ...prev, [imageKey]: "" }));
    } catch (e) {
      setError(e.message);
    } finally {
      setRefining(null);
    }
  };

  const publish = async () => {
    setPublishing(true);
    setError(null);
    try {
      const res = await fetch((process.env.PUBLIC_URL || '') + "/api/articles/detail/" + articleId + "/publish", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          category_ids: selectedCategoryIds,
          tag_ids: selectedTagIds,
          new_tag_names: newTagNames,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "שגיאה " + res.status);
      }
      const data = await res.json();
      setPublishResult(data);
      onPublished?.(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setPublishing(false);
    }
  };

  const totalImages = images ? [images.featured, ...(images.inline || [])].filter(Boolean).length : 0;

  const chipStyle = (active) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "4px 10px",
    borderRadius: 999,
    border: "1px solid " + (active ? "var(--accent)" : "var(--border)"),
    background: active ? "var(--accent-soft)" : "var(--bg)",
    color: active ? "var(--accent)" : "var(--text-secondary)",
    fontSize: 12,
    cursor: "pointer",
    userSelect: "none",
    transition: "all 0.12s",
  });

  const ImageCard = ({ label, imageKey, imageData }) => (
    <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: "14px", marginBottom: "12px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <span style={{ fontWeight: 600, fontSize: 13, color: "var(--text-secondary)" }}>{label}</span>
        {imageData?.size_kb && <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{imageData.size_kb}KB</span>}
      </div>
      {imageData?.b64 && (
        <img
          src={"data:image/jpeg;base64," + imageData.b64}
          alt={imageData.alt || ""}
          style={{ width: "100%", borderRadius: 6, maxHeight: 200, objectFit: "cover", marginBottom: 8 }}
        />
      )}
      {imageData?.alt && (
        <div style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 8 }}>Alt: {imageData.alt}</div>
      )}
      <div style={{ display: "flex", gap: 6 }}>
        <input
          type="text"
          placeholder="דייק בעברית... (Enter לאישור)"
          value={refinement[imageKey] || ""}
          onChange={(e) => setRefinement((prev) => ({ ...prev, [imageKey]: e.target.value }))}
          onKeyDown={(e) => e.key === "Enter" && regenerate(imageKey)}
          className="form-input"
          dir="rtl"
          style={{ flex: 1, fontSize: 13 }}
          disabled={refining === imageKey}
        />
        <button onClick={() => regenerate(imageKey)} disabled={refining === imageKey} className="btn btn-secondary" style={{ whiteSpace: "nowrap", fontSize: 12 }}>
          {refining === imageKey ? "⏳" : "🔄"}
        </button>
      </div>
    </div>
  );

  const totalTaxonomySelections =
    selectedCategoryIds.length + selectedTagIds.length + newTagNames.length;

  return (
    <div style={{ marginTop: 24, borderTop: "1px solid var(--border)", paddingTop: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15 }}>תמונות למאמר</div>
          {images && !publishResult && (
            <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>
              {totalImages} תמונות מוכנות - עדיין לא הועלו לאתר
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {images && !publishResult && (
            <>
              <button
                onClick={() => setShowTaxPanel((v) => !v)}
                className="btn btn-secondary"
                style={{ fontSize: 13 }}
                title="קטגוריות ותגיות"
              >
                🏷 {totalTaxonomySelections > 0 ? totalTaxonomySelections : "קטגוריות ותגיות"}
              </button>
              <button onClick={publish} disabled={publishing} className="btn btn-primary" style={{ fontSize: 13 }}>
                {publishing ? "⏳ מעלה לאתר..." : "📤 העלה לאתר"}
              </button>
            </>
          )}
          <button onClick={generateImages} disabled={loading} className="btn btn-secondary" style={{ fontSize: 13 }}>
            {loading ? "⏳" : images ? "🔄 צור מחדש" : "✨ צור תמונות"}
          </button>
        </div>
      </div>

      {/* Taxonomies panel */}
      {showTaxPanel && images && !publishResult && (
        <div style={{
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius)",
          padding: 16,
          marginBottom: 14,
        }}>
          {taxonomiesLoading && (
            <div style={{ fontSize: 13, color: "var(--text-secondary)" }}>
              טוען קטגוריות ותגיות מ-WordPress...
            </div>
          )}
          {taxonomiesError && (
            <div style={{ fontSize: 13, color: "var(--red)" }}>
              שגיאה: {taxonomiesError}
            </div>
          )}
          {!taxonomiesLoading && !taxonomiesError && (
            <>
              {/* Categories */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: "var(--text-secondary)" }}>
                  קטגוריות ({selectedCategoryIds.length} נבחרו)
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, maxHeight: 140, overflowY: "auto" }}>
                  {taxonomies.categories.length === 0 && (
                    <div style={{ fontSize: 12, color: "var(--text-muted)" }}>אין קטגוריות באתר</div>
                  )}
                  {taxonomies.categories.map((c) => (
                    <span
                      key={c.id}
                      style={chipStyle(selectedCategoryIds.includes(c.id))}
                      onClick={() => toggleCategory(c.id)}
                    >
                      {c.name} <span style={{ fontSize: 10, opacity: 0.7 }}>({c.count})</span>
                    </span>
                  ))}
                </div>
              </div>

              {/* Tags - existing */}
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: "var(--text-secondary)" }}>
                  תגיות קיימות ({selectedTagIds.length} נבחרו)
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, maxHeight: 140, overflowY: "auto" }}>
                  {taxonomies.tags.length === 0 && (
                    <div style={{ fontSize: 12, color: "var(--text-muted)" }}>אין תגיות באתר</div>
                  )}
                  {taxonomies.tags.map((t) => (
                    <span
                      key={t.id}
                      style={chipStyle(selectedTagIds.includes(t.id))}
                      onClick={() => toggleTag(t.id)}
                    >
                      {t.name} <span style={{ fontSize: 10, opacity: 0.7 }}>({t.count})</span>
                    </span>
                  ))}
                </div>
              </div>

              {/* Tags - new */}
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: "var(--text-secondary)" }}>
                  תגיות חדשות ({newTagNames.length}) - ייווצרו ב-WordPress
                </div>
                <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                  <input
                    type="text"
                    placeholder="שם תגית חדשה (Enter להוספה)"
                    value={newTagInput}
                    onChange={(e) => setNewTagInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addNewTag())}
                    className="form-input"
                    dir="rtl"
                    style={{ flex: 1, fontSize: 13 }}
                  />
                  <button onClick={addNewTag} className="btn btn-secondary" style={{ fontSize: 12, whiteSpace: "nowrap" }}>
                    + הוסף
                  </button>
                </div>
                {newTagNames.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {newTagNames.map((name) => (
                      <span key={name} style={{
                        ...chipStyle(true),
                        background: "var(--green-glow)",
                        borderColor: "var(--green)",
                        color: "var(--green)",
                      }}>
                        {name}
                        <span
                          onClick={() => removeNewTag(name)}
                          style={{ cursor: "pointer", marginInlineStart: 4, fontWeight: 700 }}
                        >
                          ×
                        </span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {loading && (
        <div style={{ background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.2)", borderRadius: "var(--radius)", padding: "14px 18px", marginBottom: 14, display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 18, height: 18, border: "2px solid var(--accent)", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite", flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: 14, color: "var(--accent)", fontWeight: 500 }}>{loadingStep || "מתחיל..."}</div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>כ-60-90 שניות</div>
          </div>
          <style>{"@keyframes spin { to { transform: rotate(360deg); } }"}</style>
        </div>
      )}

      {error && (
        <div style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: "var(--radius)", padding: "10px 14px", color: "var(--red)", fontSize: 13, marginBottom: 12, display: "flex", justifyContent: "space-between" }}>
          <span>שגיאה: {error}</span>
          <span onClick={() => setError(null)} style={{ cursor: "pointer" }}>✕</span>
        </div>
      )}

      {publishResult && (
        <div style={{ background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.2)", borderRadius: "var(--radius)", padding: "14px 16px", marginBottom: 14 }}>
          <div style={{ fontWeight: 600, color: "var(--green)", marginBottom: 6 }}>✅ הועלה ל-WordPress כטיוטה</div>
          <div style={{ fontSize: 13, color: "var(--text-secondary)" }}>
            {publishResult.images_uploaded} תמונות הועלו
            {publishResult.categories_applied?.length > 0 && " · " + publishResult.categories_applied.length + " קטגוריות"}
            {publishResult.tags_applied?.length > 0 && " · " + publishResult.tags_applied.length + " תגיות"}
          </div>
          {publishResult.wp_post_url && (
            <a href={publishResult.wp_post_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: "var(--accent)", marginTop: 4, display: "block" }}>
              פתח את הטיוטה ב-WordPress →
            </a>
          )}
        </div>
      )}

      {images && !publishResult && (
        <div>
          {images.featured && (
            <ImageCard label="תמונה ראשית (Featured)" imageKey="featured" imageData={images.featured} />
          )}
          {images.inline?.map((img, i) => (
            <ImageCard key={i} label={"תמונה " + (i + 1) + " - אחרי: \"" + img.insert_after_heading + "\""} imageKey={"inline_" + i} imageData={img} />
          ))}
        </div>
      )}
    </div>
  );
}
